import React, {useRef, useState, useEffect, useContext} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {InboxContext} from "routes/MessagesPage";
import {
  closeGeneralError,
  openGeneralError,
} from "redux/actions/settingsActions";
import {getInboxData, updateGuest} from "redux/actions/guestsActions";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import ThreadCardComponent from "./ThreadCardComponent";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import ContextMenuItem from "connectui/component/input/context/ContextMenuItem";
import Text from "connectui/component/display/Text";
import Divider from "connectui/component/display/Divider";
import ListComponent from "connectui/component/layout/list/ListComponent";
import VirtualizedListComponent from "connectui/component/layout/list/VirtualizedListComponent";
import {getHouses} from "redux/actions/listingsActions";
import {fieldParams} from "configuration/specs";
import {createAction} from "redux/actions/experiencesActions";
const rowHeight = 100;
const conversationStages = fieldParams["conversation_stage"].options;

const useStyles = (theme) =>
  buildStyles({
    list: {
      outline: "none",
      "&::-webkit-scrollbar": {width: "0!important"},
    },
  });

const ThreadList = ({
  data,
  totalRows,
  removeGuestFromList,
  skeleton = {enabled: false},
  onGuestClick,
  stageCounts,
  setStageCounts,
  currentStage,
  archivedOpen,
  ...props
}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const {guest_id} = useParams();
  const dispatch = useDispatch();
  const guestClicked = useRef(null);
  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const houses = useSelector((state) => state.defaultReducer.house_data_dict);
  const loading = useSelector((state) => state.defaultReducer.loading);
  const current_user = useSelector(
    (state) => state.defaultReducer.current_user,
  );
  const {
    mobileThreadListPosition,
    setMobileThreadListPosition,
    setGuestUpdateInProgress,
    setThreads,
  } = useContext(InboxContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrollToPosition, setScrollToPosition] = useState(null);
  const searchParams = new URLSearchParams(window.location.search);
  const viewId = searchParams.get("view_id");
  const {t} = useTranslation();
  const listLoaded = useRef(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current && isMobile) {
      if (mobileThreadListPosition > 0 && !listLoaded.current) {
        setScrollToPosition(mobileThreadListPosition);
      }
      listLoaded.current = true;
    }
  }, [mobileThreadListPosition, isMobile]);

  useEffect(() => {
    if (isMobile && !loading.listings) {
      const timer = setTimeout(() => {
        const missingListings = new Set();
        data.forEach((guest) => {
          const listingId =
            guest.booking_status === "inquiry"
              ? guest.inquiries[guest.inquiries.length - 1]?.listing_id
              : guest?.listings?.[0];

          if (listingId && !houses[listingId]) {
            missingListings.add(listingId);
          }
        });

        if (missingListings.size > 0) {
          Promise.all(
            Array.from(missingListings).map((listingId) =>
              dispatch(
                getHouses(
                  `listing_id=${listingId}&enso_key=${current_user}`,
                  true,
                  () => {},
                  () => {},
                  true,
                ),
              ),
            ),
          );
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isMobile, data, houses, loading.listings]);

  const editGuest = (guest_id, field, val) => {
    setAnchorEl(null);
    dispatch(
      updateGuest({
        guest_id,
        field: field,
        val: val,
        disableAlert: field !== "guest_type",
        onSuccess: () => dispatch(getInboxData()),
        onError: () => {
          dispatch(
            openGeneralError({
              message: "Failed to update guest",
              subtitle:
                "There was an error updating the guest. Please try again.",
              disableSeverity: true,
              onClose: () => dispatch(closeGeneralError()),
            }),
          );
        },
      }),
    );
  };

  const toggleArchive = (guest) => {
    const willBeArchived = !guest?.tags?.includes("archived");
    const updatedTags = willBeArchived
      ? [...(guest.tags ?? []), "archived"]
      : (guest.tags ?? []).filter((t) => t !== "archived");

    editGuest(guest.guest_id, "tags", updatedTags);
    removeGuestFromList(guest);

    if (stageCounts && guest.conversation_stage) {
      setStageCounts((prev) => ({
        ...prev,
        archived: willBeArchived
          ? (prev.archived || 0) + 1
          : Math.max(0, (prev.archived || 0) - 1),
        [guest.conversation_stage]: willBeArchived
          ? Math.max(0, (prev[guest.conversation_stage] || 0) - 1)
          : (prev[guest.conversation_stage] || 0) + 1,
      }));
    }
  };

  const handleGuestClick = (guest) => {
    guestClicked.current = guest;

    if (!!guest.unread_messages) {
      editGuest(guest.guest_id, "unread_messages", false);
    }

    if (guest.guest_id) {
      history.push(
        `/admin/messages/${isMobile ? "chat/" : ""}${guest.guest_id}${viewId !== null ? `?view_id=${viewId}` : ""}`,
      );
    }
    onGuestClick && onGuestClick(guest);
  };

  const updateStageCounts = (oldStage, newStage) => {
    if (!stageCounts || !oldStage || !newStage) return;

    setStageCounts((prev) => ({
      ...prev,
      [oldStage]: Math.max(0, (prev[oldStage] || 0) - 1),
      [newStage]: (prev[newStage] || 0) + 1,
    }));
  };

  const followUp = (guest) => {
    if (!guest) return;

    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(8, 0, 0, 0); // 8:00 AM tomorrow;
    const timestamp = date.getTime();

    setGuestUpdateInProgress(true);
    history.push("/admin/messages");

    const action = {
      action_id: "update_guest",
      id: "followup",
      body: {
        conversation_stage: "todo",
      },
    };

    const trigger = {
      trigger: "specific_time",
      trigger_value: timestamp,
    };

    dispatch(
      createAction({
        trigger,
        guestId: guest.guest_id,
        action,
        onSuccess: () => {
          changeStage(guest, "followup");
        },
        onError: () => {
          setGuestUpdateInProgress(false);
        },
      }),
    );
  };

  const changeStage = (guest, newStage) => {
    if (!guest) return;
    setGuestUpdateInProgress(true);

    dispatch(
      updateGuest({
        guest_id: guest.guest_id,
        field: "conversation_stage",
        val: newStage,
        onSuccess: () => {
          setTimeout(() => {
            setGuestUpdateInProgress(false);
          }, 2000);
        },
        onError: () => {
          setGuestUpdateInProgress(false);
        },
      }),
    );
  };

  const updateThreads = (guest) => {
    if (!guest) return;
    setThreads((prev) => prev.filter((g) => g.guest_id !== guest.guest_id));
  };

  return (
    <>
      {!!anchorEl && (
        <ContextMenu
          open
          onClose={() => setAnchorEl(null)}
          anchorEl={anchorEl}
          modalProps={{anchorOrigin: {horizontal: "start"}}}
          menuItems={[
            {
              label: !!guestClicked.current?.unread_messages
                ? t("inbox.threads.context-menu.mark-read")
                : t("inbox.threads.context-menu.mark-unread"),
              onClick: () =>
                editGuest(
                  guestClicked.current?.guest_id,
                  "unread_messages",
                  !guestClicked.current?.unread_messages,
                ),
            },
            !archivedOpen &&
              conversationStages[currentStage] !== "todo" && {
                label: t("inbox.threads.context-menu.mark.to-do"),
                onClick: () => {
                  setAnchorEl(null);
                  updateThreads(guestClicked.current);
                  changeStage(guestClicked.current, "todo");
                  updateStageCounts(
                    guestClicked.current.conversation_stage,
                    "todo",
                  );
                },
              },
            !archivedOpen &&
              conversationStages[currentStage] !== "resolved" && {
                label: t("inbox.threads.context-menu.mark.resolved"),
                onClick: () => {
                  setAnchorEl(null);
                  updateThreads(guestClicked.current);
                  changeStage(guestClicked.current, "resolved");
                  updateStageCounts(
                    guestClicked.current.conversation_stage,
                    "resolved",
                  );
                },
              },
            !archivedOpen &&
              conversationStages[currentStage] !== "followup" && {
                label: t("inbox.threads.context-menu.followup"),
                onClick: () => {
                  setAnchorEl(null);
                  updateThreads(guestClicked.current);
                  followUp(guestClicked.current);
                  updateStageCounts(
                    guestClicked.current.conversation_stage,
                    "followup",
                  );
                },
              },
            {Component: () => <Divider sx={{margin: theme.spacing(2, 0)}} />},
            {
              Component: ({label}) => (
                <ContextMenuItem
                  label={
                    <Text weight="medium" color="error500">
                      {label}
                    </Text>
                  }
                  onClick={() => toggleArchive(guestClicked.current)}
                />
              ),
              label: guestClicked.current?.tags?.includes("archived")
                ? t("inbox.threads.context-menu.unarchive")
                : t("inbox.threads.context-menu.archive"),
            },
          ]}
        />
      )}
      {skeleton.enabled ? (
        <ListComponent gap={"0px"} width={"100%"}>
          {Array.from({length: 6}).map((_, index) => (
            <ThreadCardComponent
              key={index}
              skeleton={{
                enabled: true,
                width: "100%",
                height: "100px",
              }}
            />
          ))}
        </ListComponent>
      ) : (
        <VirtualizedListComponent
          listRef={(el) => (listRef.current = el)}
          totalRows={totalRows}
          rowHeight={rowHeight}
          scrollTop={scrollToPosition}
          onScroll={({scrollTop}) => {
            setScrollToPosition(null);
            setMobileThreadListPosition(scrollTop);
            setAnchorEl(null);
          }}
          className={classes.list}
          {...props}
        >
          {(index) => {
            const guest = guests[data[index]?.guest_id] ?? data[index];
            let house = null;
            if (isMobile) {
              const listingId =
                guest?.booking_status === "inquiry"
                  ? guest.inquiries?.[guest.inquiries?.length - 1]?.listing_id
                  : guest?.listings?.[0];
              house = houses[listingId];
            }

            return (
              <ThreadCardComponent
                guest={guest}
                house={house}
                selected={guest?.guest_id === guest_id}
                openMenu={(e) => {
                  e.stopPropagation();
                  guestClicked.current = guest;
                  setAnchorEl(e.currentTarget);
                }}
                onClick={() => handleGuestClick(guest)}
                skeleton={{
                  enabled: index >= data.length,
                  width: "100%",
                  height: "100px",
                }}
              />
            );
          }}
        </VirtualizedListComponent>
      )}
    </>
  );
};

export default ThreadList;
